/**
 * DraftBanner Component
 * Displays a banner when a form draft exists, allowing users to resume or discard
 */
import React, { useState, useEffect } from 'react';
import { Clock, RotateCcw, X } from 'lucide-react';
import { formatDate } from '@repo/shared-utils';

export interface DraftBannerProps {
  /** Whether a draft exists */
  hasDraft: boolean;
  /** Timestamp of last save (ISO string) */
  lastSaved: string | null;
  /** Callback when user clicks to resume draft */
  onResume: () => void;
  /** Callback when user clicks to discard draft */
  onDiscard: () => void;
  /** Optional className for styling */
  className?: string;
  /** When set, hide the banner if lastSaved is within this many ms (reduces "just now" noise) */
  hideWhenRecentMs?: number;
}

export const DraftBanner: React.FC<DraftBannerProps> = ({
  hasDraft,
  lastSaved,
  onResume,
  onDiscard,
  className = '',
  hideWhenRecentMs,
}) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  if (!hasDraft || !lastSaved) return null;

  if (hideWhenRecentMs != null && hideWhenRecentMs > 0) {
    const diffMs = now - new Date(lastSaved).getTime();
    if (diffMs < hideWhenRecentMs) return null;
  }

  const formatLastSaved = (isoString: string): string => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    // For older drafts, show the date
    return formatDate(date.toISOString(), 'short') + ' ' + date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div
      role="alert"
      aria-live="polite"
      className={`bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-center justify-between ${className}`}
    >
      <div className="flex items-center flex-1 min-w-0">
        <Clock 
          size={18} 
          className="text-amber-600 dark:text-amber-400 mr-3 flex-shrink-0" 
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
            Draft saved {formatLastSaved(lastSaved)}
          </p>
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
            You have unsaved changes. Would you like to resume?
          </p>
        </div>
      </div>
      <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
        <button
          type="button"
          onClick={onResume}
          className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition flex items-center"
          aria-label="Resume draft"
        >
          <RotateCcw size={14} className="mr-1.5" aria-hidden="true" />
          Resume
        </button>
        <button
          type="button"
          onClick={onDiscard}
          className="p-1.5 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded-lg transition"
          aria-label="Discard draft"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};
